import React from 'react';
import type { HorseHealth } from '../../../../types/horseProfile';

interface HealthSectionProps {
  health: HorseHealth;
}

const DEFAULT_HEALTH: HorseHealth = {
  diet: {
    feedingSchedule: '',
    specialDiet: '',
    supplements: [],
    restrictions: []
  },
  medicalHistory: [],
  conditions: [],
  lastVetVisit: null,
  nextVetVisit: null
};

export const HealthSection: React.FC<HealthSectionProps> = ({ health = DEFAULT_HEALTH }) => {
  if (!health) return null;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ernährung</h4>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          {health.diet?.feedingSchedule && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Fütterungsplan:</strong> {health.diet.feedingSchedule}
            </p>
          )}
          {health.diet?.specialDiet && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
              <strong>Spezielle Ernährung:</strong> {health.diet.specialDiet}
            </p>
          )}
          {health.diet?.supplements && health.diet.supplements.length > 0 && (
            <div className="mt-2">
              <strong className="text-sm text-gray-700 dark:text-gray-300">Ergänzungsfutter:</strong>
              <ul className="mt-1 list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {health.diet.supplements.map((supplement, index) => (
                  <li key={index}>{supplement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {health.medicalHistory && health.medicalHistory.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Medizinische Historie</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Beschreibung
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tierarzt
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {health.medicalHistory.map((record, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(record.date).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {record.type === 'vaccination' ? 'Impfung' :
                       record.type === 'injury' ? 'Verletzung' :
                       record.type === 'checkup' ? 'Kontrolle' : 'Behandlung'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {record.description}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {record.veterinarian}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {health.conditions && health.conditions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Gesundheitliche Einschränkungen</h4>
          <div className="space-y-4">
            {health.conditions.map((condition, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 dark:text-white">{condition.name}</h5>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>Diagnose:</strong> {condition.diagnosis}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>Behandlung:</strong> {condition.treatment}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>Diagnostiziert:</strong> {new Date(condition.diagnosed).toLocaleDateString('de-DE')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
        <p>
          <strong>Letzter Tierarztbesuch:</strong>{' '}
          {health.lastVetVisit ? new Date(health.lastVetVisit).toLocaleDateString('de-DE') : 'Nicht angegeben'}
        </p>
        {health.nextVetVisit && (
          <p>
            <strong>Nächster Termin:</strong>{' '}
            {new Date(health.nextVetVisit).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>
    </div>
  );
};